// Pure helpers for the chart RPM (BPP) graph. No React / Chart.js imports so
// they can be unit-tested with plain node.
//
// Patients usually take 2-3 cuff readings per sitting, a minute or two apart.
// On a months-wide time axis those readings land on the same pixel column and
// the line turns into vertical zig-zags. The graph plots ONE point per sitting
// (the average of its readings); the readings table keeps every reading.

// A sitting = readings taken within this many minutes of its first reading.
export const SITTING_WINDOW_MINUTES = 30;

// "YYYY-MM-DD HH:MM[:SS]" or "YYYY-MM-DDTHH:MM[:SS]" (bpp-master `datetime`)
const ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?/;
// "MM-DD-YYYY hh:mm AM" (bpp-master `datelabel`)
const LABEL_RE = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?/;

// Parse an RPM timestamp as local time. Both API shapes are parsed explicitly
// because Safari's Date parser rejects them; anything else falls back to Date.
export function parseRpmTimestamp(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const s = String(value).trim();

  let m = s.match(ISO_RE);
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
    return isNaN(d) ? null : d;
  }
  m = s.match(LABEL_RE);
  if (m) {
    let hour = +m[4];
    const ampm = (m[7] || "").toUpperCase();
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    const d = new Date(+m[3], +m[1] - 1, +m[2], hour, +m[5], +(m[6] || 0));
    return isNaN(d) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

const toFiniteNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// readings: [{ time: Date, values: { Systolic: 142, Diastolic: 88, ... } }], any order.
// Returns sittings sorted by time:
//   { start, end, count, values: { key: rounded average | null }, readings: { key: [raw values] } }
export function groupIntoSittings(readings, windowMinutes = SITTING_WINDOW_MINUTES) {
  const windowMs = windowMinutes * 60 * 1000;
  const sorted = (readings || [])
    .filter((r) => r && r.time instanceof Date && !isNaN(r.time))
    .slice()
    .sort((a, b) => a.time - b.time);

  const groups = [];
  let current = null;
  for (const r of sorted) {
    if (!current || r.time - current[0].time > windowMs) {
      current = [r];
      groups.push(current);
    } else {
      current.push(r);
    }
  }

  return groups.map((group) => {
    const keys = new Set();
    group.forEach((r) => Object.keys(r.values || {}).forEach((k) => keys.add(k)));

    const values = {};
    const raw = {};
    keys.forEach((k) => {
      const nums = group.map((r) => toFiniteNumber(r.values?.[k])).filter((n) => n !== null);
      raw[k] = nums;
      values[k] = nums.length
        ? Math.round(nums.reduce((sum, n) => sum + n, 0) / nums.length)
        : null;
    });

    return {
      start: group[0].time,
      end: group[group.length - 1].time,
      count: group.length,
      values,
      readings: raw,
    };
  });
}
